import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import InfoTooltip from '../components/InfoTooltip';

const API_BASE = '/api';

// Category display config
const CATEGORY_CONFIG = {
  database: {
    label: 'DATABASE',
    bgColor: 'bg-green-50',
    headerBg: 'bg-green-600',
    headerText: 'text-white',
    borderColor: 'border-green-200',
    accentColor: 'text-green-700',
    tagBg: 'bg-green-100 text-green-800',
    icon: '\uD83D\uDCCA',
  },
  grassroots: {
    label: 'GRASSROOTS',
    bgColor: 'bg-yellow-50',
    headerBg: 'bg-yellow-500',
    headerText: 'text-white',
    borderColor: 'border-yellow-200',
    accentColor: 'text-yellow-700',
    tagBg: 'bg-yellow-100 text-yellow-800',
    icon: '\uD83C\uDF31',
  },
  strategic_partners: {
    label: 'STRATEGIC PARTNERS',
    bgColor: 'bg-blue-50',
    headerBg: 'bg-blue-700',
    headerText: 'text-white',
    borderColor: 'border-blue-200',
    accentColor: 'text-blue-700',
    tagBg: 'bg-blue-100 text-blue-800',
    icon: '\uD83E\uDD1D',
  },
};

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(monthStr) {
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Shift month by N months
function shiftMonth(monthStr, delta) {
  const [year, month] = monthStr.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export default function MonthlyPage() {
  const { token } = useAuth();
  const [habits, setHabits] = useState([]);
  const [monthlyLogs, setMonthlyLogs] = useState({});
  const [dailyScores, setDailyScores] = useState([]);
  const [totalMonthlyXp, setTotalMonthlyXp] = useState(0);
  const [dailyXpTotal, setDailyXpTotal] = useState(0);
  const [weeklyHabitXp, setWeeklyHabitXp] = useState(0);
  const [monthlyHabitXp, setMonthlyHabitXp] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [previousMonth, setPreviousMonth] = useState(null);
  const [showPerfectMonthMessage, setShowPerfectMonthMessage] = useState(false);
  const perfectMonthShownRef = useRef(null);

  const currentMonth = getCurrentMonth();
  const today = new Date().toISOString().split('T')[0];

  // Month navigation state
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const isCurrentMonth = selectedMonth === currentMonth;
  const canGoForward = selectedMonth < currentMonth;

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      setSelectedMonth(prev => shiftMonth(prev, -1));
    } else if (direction === 'next' && canGoForward) {
      setSelectedMonth(prev => shiftMonth(prev, 1));
    }
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(currentMonth);
  };

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [habitsRes, logsRes, scoresRes, prevMonthRes] = await Promise.all([
        fetch(`${API_BASE}/habits`, { headers }),
        fetch(`${API_BASE}/logs/month?month=${selectedMonth}`, { headers }),
        fetch(`${API_BASE}/scores/monthly?month=${selectedMonth}`, { headers }),
        fetch(`${API_BASE}/scores/monthly?month=${shiftMonth(selectedMonth, -1)}`, { headers }).catch(() => null),
      ]);

      const habitsData = await habitsRes.json();
      const logsData = await logsRes.json();
      const scoresData = await scoresRes.json();

      // Filter to only monthly habits
      const monthlyHabits = (habitsData.habits || []).filter(h => h.frequency === 'monthly');
      setHabits(monthlyHabits);

      // Index monthly logs by habit_id (find any log for this month for each monthly habit)
      const logsMap = {};
      (logsData.logs || []).forEach(l => {
        if (l.frequency === 'monthly') {
          if (!logsMap[l.habit_id] || l.log_date > logsMap[l.habit_id].log_date) {
            logsMap[l.habit_id] = l;
          }
        }
      });
      setMonthlyLogs(logsMap);

      setDailyScores(scoresData.scores || []);
      setTotalMonthlyXp(scoresData.totalMonthlyXp || 0);
      setDailyXpTotal(scoresData.dailyXpTotal || 0);
      setWeeklyHabitXp(scoresData.weeklyHabitXp || 0);
      setMonthlyHabitXp(scoresData.monthlyHabitXp || 0);

      // Set previous month data
      if (prevMonthRes && prevMonthRes.ok) {
        const prevMonthData = await prevMonthRes.json();
        setPreviousMonth({
          month: shiftMonth(selectedMonth, -1),
          totalMonthlyXp: prevMonthData.totalMonthlyXp || 0,
        });
      } else {
        setPreviousMonth(null);
      }
    } catch (err) {
      console.error('Error loading monthly view:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Count completed monthly habits (declared before perfect-month calc that depends on them)
  const completedCount = habits.filter(h => monthlyLogs[h.id]?.completed).length;
  const totalMonthlyHabits = habits.length;

  // Check for perfect month (all daily targets met every day + all monthly habits completed)
  const allMonthlyHabitsComplete = totalMonthlyHabits > 0 && completedCount === totalMonthlyHabits;
  const allDailyTargetsMet = dailyScores.length > 0 && dailyScores.every(score => score.all_targets_met === 1);
  const isPerfectMonth = allMonthlyHabitsComplete && allDailyTargetsMet;

  // Trigger celebration when transitioning to perfect month
  useEffect(() => {
    if (!loading && isCurrentMonth && !showPerfectMonthMessage) {
      if (isPerfectMonth && perfectMonthShownRef.current !== selectedMonth) {
        setShowPerfectMonthMessage(true);
        perfectMonthShownRef.current = selectedMonth;
      }
    }
  }, [isPerfectMonth, loading, isCurrentMonth, showPerfectMonthMessage, selectedMonth]);

  const handleToggle = async (habitId) => {
    if (!isCurrentMonth) return; // Safety: don't allow edits for past months
    setToggling(habitId);
    try {
      const res = await fetch(`${API_BASE}/logs/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ habit_id: habitId, date: today }),
      });
      const data = await res.json();
      if (res.ok && data.log) {
        setMonthlyLogs(prev => ({ ...prev, [habitId]: { ...data.log, frequency: 'monthly' } }));
        // Refresh scores
        const headers = { Authorization: `Bearer ${token}` };
        const scoresRes = await fetch(`${API_BASE}/scores/monthly?month=${selectedMonth}`, { headers });
        const scoresData = await scoresRes.json();
        setTotalMonthlyXp(scoresData.totalMonthlyXp || 0);
        setDailyXpTotal(scoresData.dailyXpTotal || 0);
        setWeeklyHabitXp(scoresData.weeklyHabitXp || 0);
        setMonthlyHabitXp(scoresData.monthlyHabitXp || 0);
      }
    } catch (err) {
      console.error('Error toggling habit:', err);
    } finally {
      setToggling(null);
    }
  };

  const monthlyProgressPercent = totalMonthlyHabits > 0
    ? Math.round((completedCount / totalMonthlyHabits) * 100)
    : 0;

  // Group monthly habits by category
  const groupedHabits = {};
  habits.forEach(habit => {
    if (!groupedHabits[habit.category]) {
      groupedHabits[habit.category] = [];
    }
    groupedHabits[habit.category].push(habit);
  });

  const categoryOrder = ['database', 'grassroots', 'strategic_partners'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="monthly-page">
      {/* Perfect Month Celebration Message */}
      {showPerfectMonthMessage && (
        <div
          className="mb-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-lg p-6 text-center shadow-lg animate-pulse"
          data-testid="perfect-month-message"
        >
          <div className="text-4xl mb-2">🏆🎉🏆</div>
          <h2 className="text-2xl font-bold text-white mb-2">PERFECT MONTH!</h2>
          <p className="text-white font-medium">
            Incredible! You hit all daily targets every day AND completed all monthly habits!
          </p>
          <button
            onClick={() => setShowPerfectMonthMessage(false)}
            className="mt-3 px-4 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
            data-testid="dismiss-perfect-month"
          >
            Thanks!
          </button>
        </div>
      )}

      {/* Month Navigation Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300"
            data-testid="month-nav-prev"
            aria-label="Previous month"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white" data-testid="monthly-title">Monthly View</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1" data-testid="month-label">
              {isCurrentMonth
                ? formatMonthLabel(selectedMonth)
                : `${formatMonthLabel(selectedMonth)} (Read-Only)`
              }
            </p>
          </div>

          <button
            onClick={() => navigateMonth('next')}
            disabled={!canGoForward}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="month-nav-next"
            aria-label="Next month"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Return to Current Month button */}
        {!isCurrentMonth && (
          <div className="mt-3 text-center">
            <button
              onClick={goToCurrentMonth}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors text-sm font-medium"
              data-testid="month-nav-current"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Back to Current Month
            </button>
          </div>
        )}
      </div>

      {/* Past Month Read-Only Banner */}
      {!isCurrentMonth && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-center" data-testid="past-month-banner">
          <p className="text-sm text-amber-700 font-medium">
            {'🔒'} You are viewing a past month. Habits are read-only.
          </p>
        </div>
      )}

      {/* Monthly Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Monthly XP Total</div>
          <div className="text-2xl font-bold text-primary" data-testid="monthly-xp">
            {totalMonthlyXp}
          </div>
          <div className="text-xs text-gray-400 mt-1">Daily + Weekly + Monthly</div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Daily Habit XP</div>
          <div className="text-2xl font-bold text-green-600" data-testid="monthly-daily-xp">
            {dailyXpTotal}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Weekly Habit XP</div>
          <div className="text-2xl font-bold text-blue-600" data-testid="monthly-weekly-xp">
            {weeklyHabitXp}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Monthly Habit XP</div>
          <div className="text-2xl font-bold text-purple-600" data-testid="monthly-habit-xp">
            {monthlyHabitXp}
          </div>
        </div>
      </div>

      {/* Monthly Progress Bar (also serves as the Monthly Habits Completed count) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="monthly-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Monthly Habits Progress</span>
          <span className="text-sm text-gray-500" data-testid="monthly-habits-completed">{completedCount} / {totalMonthlyHabits} completed ({monthlyProgressPercent}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-primary rounded-full h-3 transition-all duration-500"
            style={{ width: `${Math.min(monthlyProgressPercent, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Monthly Habits by Category — logging section */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-bold text-gray-700 dark:text-gray-100">Monthly Habits</h2>
        <InfoTooltip text="These are your monthly Core Forever Engine activities. By the end of each month, mark each one Done once you've completed it to earn XP." />
      </div>

      {categoryOrder.map(category => {
        const config = CATEGORY_CONFIG[category];
        const categoryHabits = groupedHabits[category];
        if (!categoryHabits || categoryHabits.length === 0) return null;

        return (
          <div
            key={category}
            className={`rounded-lg border ${config.borderColor} ${config.bgColor} mb-4 overflow-hidden shadow-sm`}
            data-testid={`monthly-category-${category}`}
          >
            {/* Category Header */}
            <div className={`${config.headerBg} ${config.headerText} px-4 py-2 flex items-center gap-2`}>
              <span className="text-lg">{config.icon}</span>
              <h3 className="font-bold text-sm tracking-wider">{config.label}</h3>
            </div>

            {/* Habit Cards */}
            <div className="p-3 space-y-3">
              {categoryHabits.map(habit => {
                const log = monthlyLogs[habit.id];
                const isCompleted = log ? !!log.completed : false;
                const xpEarned = isCompleted ? habit.xp_per_unit : 0;
                const isToggling = toggling === habit.id;

                return (
                  <div
                    key={habit.id}
                    className={`bg-white rounded-lg p-4 border ${isCompleted ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'} transition-all`}
                    data-testid={`monthly-habit-card-${habit.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isCompleted && (
                            <span className="text-green-500 text-lg" data-testid={`monthly-habit-complete-${habit.id}`}>{'\u2705'}</span>
                          )}
                          <h4 className="font-medium text-gray-800 text-sm" data-testid={`monthly-habit-name-${habit.id}`}>
                            {habit.name}
                          </h4>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs font-medium ${isCompleted ? 'text-green-600' : 'text-gray-400'}`} data-testid={`monthly-habit-xp-${habit.id}`}>
                            {xpEarned} / {habit.xp_per_unit} XP
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.tagBg}`}>
                            {config.label}
                          </span>
                        </div>
                      </div>

                      {/* Toggle Button */}
                      <button
                        onClick={() => handleToggle(habit.id)}
                        disabled={!isCurrentMonth || isToggling}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isCompleted
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : !isCurrentMonth
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`monthly-habit-toggle-${habit.id}`}
                        aria-label={`Toggle ${habit.name}`}
                      >
                        <span className="text-lg">{isCompleted ? '\u2705' : '\u2B1C'}</span>
                        <span>{isCompleted ? 'Yes' : 'No'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Month-over-Month Comparison — reference */}
      {previousMonth && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6 mt-6" data-testid="month-comparison">
          <div className="text-xs text-gray-500 uppercase font-medium mb-3">Month-over-Month Comparison</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-400 mb-1">This Month</div>
              <div className="text-xl font-bold text-primary" data-testid="current-month-xp">{totalMonthlyXp} <span className="text-sm font-normal text-gray-400">XP</span></div>
            </div>
            <div>
              <div className="text-xs text-gray-400 mb-1">Last Month</div>
              <div className="text-xl font-bold text-gray-500" data-testid="previous-month-xp">{previousMonth.totalMonthlyXp} <span className="text-sm font-normal text-gray-400">XP</span></div>
            </div>
          </div>
          {(() => {
            const diff = totalMonthlyXp - previousMonth.totalMonthlyXp;
            const pct = previousMonth.totalMonthlyXp > 0
              ? Math.round((diff / previousMonth.totalMonthlyXp) * 100)
              : totalMonthlyXp > 0 ? 100 : 0;
            const isUp = diff > 0;
            const isDown = diff < 0;
            const isSame = diff === 0;
            return (
              <div className={`mt-3 flex items-center gap-2 text-sm font-medium ${isUp ? 'text-green-600' : isDown ? 'text-red-500' : 'text-gray-500'}`} data-testid="month-comparison-indicator">
                <span className="text-lg">{isUp ? '📈' : isDown ? '📉' : '➡️'}</span>
                <span>
                  {isSame
                    ? 'Same as last month'
                    : `${isUp ? '+' : ''}${diff} XP (${isUp ? '+' : ''}${pct}%) ${isUp ? 'growth' : 'decline'} from last month`
                  }
                </span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
